import type { FullTranscript } from '@/services/PagesServices/LearnerTranscriptService';

/**
 * Structured (selectable-text) transcript PDF export, modeled on
 * dashboardPdfExport.ts (dynamic jsPDF import, manual layout, doc.save).
 */

const COLORS = {
  primary: [44, 62, 80] as [number, number, number],
  accent: [255, 140, 66] as [number, number, number],
  text: [31, 41, 55] as [number, number, number],
  textLight: [107, 114, 128] as [number, number, number],
  border: [229, 231, 235] as [number, number, number],
  bgLight: [249, 250, 251] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

const fullName = (t: FullTranscript) =>
  `${t.profile?.first_name || ''} ${t.profile?.last_name || ''}`.trim() || t.profile?.email || 'Learner';

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleDateString();
};

async function newDoc() {
  const jspdfModule = (await import('jspdf')) as any;
  const JsPDF = jspdfModule.jsPDF || jspdfModule.default;
  return new JsPDF('p', 'mm', 'a4') as any;
}

// Render one learner's transcript onto `doc`, starting at top of the current page.
function renderTranscript(doc: any, t: FullTranscript) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkBreak = (needed: number) => {
    if (y + needed > pageHeight - 18) { doc.addPage(); y = margin; }
  };

  const sectionTitle = (label: string) => {
    checkBreak(14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.primary);
    doc.text(label, margin, y);
    y += 6;
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(margin, y, margin + contentWidth, y);
    y += 6;
  };

  // Simple table: columns = [{header, width, get}], rows = data[]
  const table = (cols: { header: string; width: number; get: (r: any) => string }[], rows: any[], emptyText: string) => {
    const rowH = 7;
    if (!rows || rows.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.textLight);
      checkBreak(rowH);
      doc.text(emptyText, margin, y + 5);
      y += rowH + 2;
      return;
    }
    // Header row
    checkBreak(rowH);
    doc.setFillColor(...COLORS.bgLight);
    doc.rect(margin, y, contentWidth, rowH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.text);
    let x = margin + 2;
    cols.forEach((c) => { doc.text(c.header, x, y + 5); x += c.width; });
    y += rowH;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    rows.forEach((r) => {
      checkBreak(rowH);
      x = margin + 2;
      cols.forEach((c) => {
        const text = doc.splitTextToSize(String(c.get(r) ?? ''), c.width - 3)[0] || '';
        doc.text(text, x, y + 5);
        x += c.width;
      });
      doc.setDrawColor(...COLORS.border);
      doc.line(margin, y + rowH, margin + contentWidth, y + rowH);
      y += rowH;
    });
    y += 4;
  };

  // ---- Header
  doc.setFillColor(...COLORS.primary);
  doc.roundedRect(margin, y, contentWidth, 26, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.white);
  doc.text('Learner Transcript', margin + 8, y + 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(fullName(t), margin + 8, y + 18);
  doc.setFontSize(8);
  doc.setTextColor(210, 218, 226);
  doc.text(t.profile?.email || '', margin + 8, y + 23);
  doc.setTextColor(...COLORS.white);
  doc.text(`Generated ${new Date().toLocaleDateString()}`, pageWidth - margin - 8, y + 11, { align: 'right' });
  y += 34;

  // ---- Summary stats
  sectionTitle('Summary');
  const s = t.stats || {};
  const summary = [
    ['Courses completed', String(s.completed_courses ?? '—')],
    ['Total courses', String(s.total_courses ?? t.courses.length)],
    ['Certificates', String(s.total_certificates ?? t.certificates.length)],
    ['Learning hours (yr)', String(Math.round(Number(t.learningHours?.totalHours || 0)))],
  ];
  doc.setFontSize(9);
  const colW = contentWidth / 4;
  summary.forEach((pair, i) => {
    const x = margin + i * colW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.accent);
    doc.text(pair[1], x + 2, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text(pair[0], x + 2, y + 11);
  });
  y += 18;

  // ---- Courses
  sectionTitle('Courses');
  table(
    [
      { header: 'Course', width: 78, get: (r) => r.title },
      { header: 'Status', width: 28, get: (r) => r.status },
      { header: 'Progress', width: 22, get: (r) => `${r.progress ?? 0}%` },
      { header: 'Enrolled', width: 27, get: (r) => fmtDate(r.enrolled_date) },
      { header: 'Completed', width: 25, get: (r) => fmtDate(r.completed_at) },
    ],
    t.courses,
    'No enrolled courses.'
  );

  // ---- Assessments
  sectionTitle('Assessments');
  table(
    [
      { header: 'Assessment', width: 80, get: (r) => r.title },
      { header: 'Status', width: 30, get: (r) => r.status },
      { header: 'Score', width: 30, get: (r) => (r.percentage != null ? `${r.percentage}%` : r.score != null ? String(r.score) : '—') },
      { header: 'Date', width: 40, get: (r) => fmtDate(r.submitted_at || r.assigned_at) },
    ],
    t.assessments,
    'No assessments.'
  );

  // ---- Certificates
  sectionTitle('Certificates');
  table(
    [
      { header: 'Certificate', width: 100, get: (r) => r.certificate_name },
      { header: 'Status', width: 40, get: (r) => r.status },
      { header: 'Issued', width: 40, get: (r) => fmtDate(r.issued_date) },
    ],
    t.certificates,
    'No certificates.'
  );
}

const fileSafe = (s: string) => s.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'learner';
const today = () => new Date().toISOString().split('T')[0];

export const exportTranscriptPDF = async (t: FullTranscript) => {
  const doc = await newDoc();
  renderTranscript(doc, t);
  doc.save(`Transcript_${fileSafe(fullName(t))}_${today()}.pdf`);
};

export const exportBulkTranscriptsPDF = async (transcripts: FullTranscript[], orgName = 'Organization') => {
  const doc = await newDoc();
  transcripts.forEach((t, i) => {
    if (i > 0) doc.addPage();
    renderTranscript(doc, t);
  });
  doc.save(`Transcripts_${fileSafe(orgName)}_${today()}.pdf`);
};
